// Document Slice
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { Document, ProcessingStatus } from '../../types';
import { documentService } from '../../services/documentService';

interface DocumentState {
  documents: Document[];
  currentDocument: Document | null;
  loading: boolean;
  error: string | null;
  uploadProgress: number;
  processingStatus: ProcessingStatus;
}

const initialState: DocumentState = {
  documents: [], // Ensure this is always an array
  currentDocument: null,
  loading: false,
  error: null,
  uploadProgress: 0,
  processingStatus: {
    status: 'idle',
    progress: 0,
    message: '',
  },
};

// Async thunks
export const fetchDocuments = createAsyncThunk(
  'documents/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await documentService.getAllDocuments();
      // Ensure we always return an array
      return Array.isArray(response) ? response : [];
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch documents');
    }
  }
);

export const fetchDocumentById = createAsyncThunk(
  'documents/fetchById',
  async (id: string, { rejectWithValue }) => {
    try {
      return await documentService.getDocumentById(id);
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch document');
    }
  }
);

export const uploadDocument = createAsyncThunk(
  'documents/upload',
  async (
    { file, onProgress }: { file: File; onProgress?: (progress: number) => void },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const response = await documentService.uploadDocument(file, (progress) => {
        dispatch(setUploadProgress(progress));
        onProgress?.(progress);
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to upload document');
    }
  }
);

export const deleteDocument = createAsyncThunk(
  'documents/delete',
  async (id: string, { rejectWithValue }) => {
    try {
      await documentService.deleteDocument(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete document');
    }
  }
);

const documentSlice = createSlice({
  name: 'documents',
  initialState,
  reducers: {
    setCurrentDocument: (state, action) => {
      state.currentDocument = action.payload;
    },
    clearCurrentDocument: (state) => {
      state.currentDocument = null;
    },
    setUploadProgress: (state, action) => {
      state.uploadProgress = action.payload;
    },
    setProcessingStatus: (state, action) => {
      state.processingStatus = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch documents
      .addCase(fetchDocuments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDocuments.fulfilled, (state, action) => {
        state.loading = false;
        // Ensure we always set an array
        state.documents = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchDocuments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || 'Failed to fetch documents';
        state.documents = []; // Set to empty array on error
      })
      
      // Fetch document by ID
      .addCase(fetchDocumentById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDocumentById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentDocument = action.payload;
      })
      .addCase(fetchDocumentById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || 'Failed to fetch document';
      })
      
      // Upload document
      .addCase(uploadDocument.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.uploadProgress = 0;
      })
      .addCase(uploadDocument.fulfilled, (state, action) => {
        state.loading = false;
        // Ensure documents is still an array before pushing
        if (Array.isArray(state.documents)) {
          state.documents.push(action.payload);
        } else {
          state.documents = [action.payload];
        }
        state.currentDocument = action.payload;
        state.uploadProgress = 100;
      })
      .addCase(uploadDocument.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || 'Failed to upload document';
        state.uploadProgress = 0;
      })
      
      // Delete document
      .addCase(deleteDocument.fulfilled, (state, action) => {
        if (Array.isArray(state.documents)) {
          state.documents = state.documents.filter(doc => doc._id !== action.payload);
        }
        if (state.currentDocument?._id === action.payload) {
          state.currentDocument = null;
        }
      });
  },
});

export const {
  setCurrentDocument,
  clearCurrentDocument,
  setUploadProgress,
  setProcessingStatus,
  clearError,
} = documentSlice.actions;

export default documentSlice.reducer;